import * as React from "react";
import { ContactCard } from "./ContactCard";
import Icon24 from "../../img/b19d0cba7caca5d2da234a859885e15d09d00fa106cea4702598366c599dbda2.svg";

function ContactAddButton({
  disabled = false,
  onClick,
  alwaysEnabled = false,
}) {
  const isDisabled = !alwaysEnabled && disabled;

  return (
    <div className="contact-add-btn-wrap relative inline-block">
      <button
        type="button"
        className={`contact-add-btn text-sm px-2 py-1 rounded transition-all duration-300 group whitespace-nowrap w-6 ${
          isDisabled
            ? "bg-gray-400 text-gray-600 cursor-not-allowed"
            : "bg-red-600 text-white hover:bg-red-700"
        }`}
        onClick={() => {
          if (isDisabled) return;
          onClick();
        }}
        disabled={isDisabled}
      >
        <span className={isDisabled ? "text-gray-600" : "group-hover:hidden"}>
          +
        </span>
        <span
          className={`contact-add-btn-label absolute left-0 top-0 text-sm px-2 py-1 rounded whitespace-nowrap z-10 ${
            isDisabled
              ? "hidden"
              : "hidden group-hover:block bg-red-600 text-white"
          }`}
        >
          + Add Contact
        </span>
      </button>
    </div>
  );
}

function RoleHeader({ title, hideUpdate, addButton }) {
  return (
    <div className="contact-role-header flex items-center justify-start mb-4 w-full gap-2">
      <div className="contact-role-label text-lg font-medium text-zinc-800 min-w-0">
        {title}:
      </div>
      {!hideUpdate && addButton}
    </div>
  );
}

function openAddContactModal(sectionTitle, contactType) {
  const event = new CustomEvent("openContactModal", {
    detail: {
      sectionType: sectionTitle,
      ...(contactType ? { contactType } : {}),
    },
  });
  window.dispatchEvent(event);
}

export function ContactSection({ title, sections, sectionType, onRefresh, hideUpdate }) {
  // Add state for section visibility
  const [isExpanded, setIsExpanded] = React.useState(true);
  
  // Check if it's the Vendor section
  const isVendorSection = sectionType === "Vendor";
  const isNettlesSection = sectionType === "customerContacts";

  // Helper function to check if a contact exists for a specific type in Nettles Project Team
  const hasContactForType = (group) => {
    if (!isNettlesSection) return false;
    // Allow multiple Superintendents, so don't disable the add button
    if (group.title === "Superintendent") return false;
    return group.contacts && group.contacts.length > 0;
  };

  // If sections is undefined or null, return early with a message
  if (!sections || !Array.isArray(sections)) {
    return (
      <div className="contact-section flex flex-col w-full mb-6">
        <div className="contact-section-card flex flex-col px-12 py-7 w-full bg-white rounded-xl shadow-md max-md:px-5 max-md:max-w-full">
          <div className="contact-section-header flex items-center justify-between pb-4 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <div className="contact-section-title text-xl font-semibold text-zinc-800">
                {title}
              </div>
            </div>
          </div>
          <div className="contact-section-body mt-6">
            <div className="contact-role-empty text-gray-500 italic">No contacts available</div>
          </div>
        </div>
      </div>
    );
  }

  const renderRoleAddButton = (group, options = {}) => (
    <ContactAddButton
      disabled={hasContactForType(group)}
      alwaysEnabled={options.alwaysEnabled}
      onClick={() => openAddContactModal(title, group.title)}
    />
  );

  const renderContactList = (group, { hasChat, showLatestOnly = false } = {}) => {
    if (!group.contacts || group.contacts.length === 0) {
      return <div className="contact-role-empty text-gray-500 italic mt-2">No contact found</div>;
    }

    if (showLatestOnly && !hideUpdate) {
      return (
        <ContactCard
          contact={group.contacts[group.contacts.length - 1]}
          hasChat={hasChat}
          sectionType={title}
          onSuccess={onRefresh}
          disablePopup={hideUpdate}
        />
      );
    }

    return group.contacts.map((contact, cIndex) => (
      <div key={cIndex} className="contact-role-contact">
        <ContactCard
          contact={contact}
          hasChat={hasChat}
          sectionType={title}
          onSuccess={onRefresh}
          disablePopup={hideUpdate}
        />
        {cIndex < group.contacts.length - 1 && (
          <div className="contact-card-divider h-px bg-gray-200 my-4" />
        )}
      </div>
    ));
  };

  return (
    <div className="contact-section flex flex-col w-full mb-6">
      <div className="contact-section-card flex flex-col px-12 py-7 w-full bg-white rounded-xl shadow-md max-md:px-5 max-md:max-w-full">
        <div className="contact-section-header flex items-center justify-between pb-4 border-b border-gray-200">
          <div className="contact-section-header-left flex items-center gap-4 min-w-0">
            <div className="contact-section-title text-xl font-semibold text-zinc-800">
              {title}
            </div>
            {!hideUpdate && isVendorSection && (
              <ContactAddButton
                alwaysEnabled
                onClick={() => openAddContactModal(title)}
              />
            )}
          </div>
          {/* Add click handler to toggle section */}
          <img
            loading="lazy"
            src={Icon24}
            className={`contact-section-toggle aspect-square w-[12px] cursor-pointer transition-transform duration-300 ${
              isExpanded ? "rotate-180" : ""
            }`}
            alt={isExpanded ? "Collapse section" : "Expand section"}
            onClick={() => setIsExpanded(!isExpanded)}
          />
        </div>
        {/* Conditionally render content based on isExpanded state */}
        {isExpanded && (
          <div className="contact-section-body mt-6">
            {isNettlesSection ? (
              // Special rendering for Nettles Project Team with Superintendent in second row
              <div className="contact-roles-stack space-y-6">
                {/* First row - first 5 contact types */}
                <div className="contact-roles-grid grid grid-cols-5 gap-x-10 gap-y-6 items-start w-full max-md:grid-cols-2 max-md:max-w-full max-sm:grid-cols-[1fr]">
                  {sections.slice(0, 5).map((group, index) => (
                    <div key={index} className="contact-role-block flex flex-col w-full">
                      {group.contacts && group.contacts.length > 0 ? (
                        <div className="flex flex-col">
                          <RoleHeader
                            title={group.title}
                            hideUpdate={hideUpdate}
                            addButton={renderRoleAddButton(group)}
                          />
                          {renderContactList(group, {
                            hasChat: true,
                            showLatestOnly: true,
                          })}
                        </div>
                      ) : (
                        <>
                          <RoleHeader
                            title={group.title}
                            hideUpdate={hideUpdate}
                            addButton={renderRoleAddButton(group)}
                          />
                          <div className="contact-role-empty text-gray-500 italic mt-2">
                            No contact found
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Second row - Superintendent, same column width as roles above */}
                {sections.slice(5).map((group, index) => (
                  <div key={index + 5} className="contact-role-block contact-role-block--full w-full">
                    <div className="contact-roles-grid grid grid-cols-5 gap-x-10 gap-y-6 items-start w-full max-md:grid-cols-2 max-md:max-w-full max-sm:grid-cols-[1fr]">
                      {(group.contacts && group.contacts.length > 0
                        ? group.contacts
                        : [null]
                      ).map((contact, cIndex) => (
                        <div key={cIndex} className="contact-role-block flex flex-col w-full">
                          {cIndex === 0 ? (
                            <RoleHeader
                              title={group.title}
                              hideUpdate={hideUpdate}
                              addButton={renderRoleAddButton(group, { alwaysEnabled: true })}
                            />
                          ) : (
                            <div className="contact-role-header mb-4 w-full" aria-hidden="true" />
                          )}
                          {contact ? (
                            <div className="contact-role-contact">
                              <ContactCard
                                contact={contact}
                                hasChat={isNettlesSection}
                                sectionType={title}
                                onSuccess={onRefresh}
                                disablePopup={hideUpdate}
                              />
                            </div>
                          ) : (
                            <div className="contact-role-empty text-gray-500 italic mt-2">
                              No contact found
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : isVendorSection ? (
              // Vendor section rendering
              <div className="contact-vendor-list flex flex-wrap gap-10 max-md:gap-4">
                {sections.map((group, index) => (
                  <div key={index} className="contact-role-block w-full">
                    {group.contacts && group.contacts.length > 0 ? (
                      <div className="contact-role-multi flex flex-row flex-wrap gap-10 max-md:gap-4 max-md:flex-col">
                        {group.contacts.map((contact, cIndex) => (
                          <div
                            key={cIndex}
                            className="contact-role-contact flex-shrink-0 max-md:w-full"
                            style={{ minWidth: "300px", maxWidth: "400px" }}
                          >
                            <ContactCard
                              contact={contact}
                              hasChat={false}
                              sectionType={title}
                              onSuccess={onRefresh}
                              disablePopup={hideUpdate}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="contact-role-empty text-gray-500 italic mt-2">
                        No contact found
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              // Standard rendering for other sections
              <div className="contact-roles-grid grid grid-cols-5 gap-x-10 gap-y-6 items-start w-full max-md:grid-cols-2 max-md:max-w-full max-sm:grid-cols-[1fr]">
                {sections.map((group, index) => (
                  <div key={index} className="contact-role-block flex flex-col w-full">
                    {group.contacts && group.contacts.length > 0 ? (
                      <div className="flex flex-col">
                        <RoleHeader
                          title={group.title}
                          hideUpdate={hideUpdate}
                          addButton={renderRoleAddButton(group, { alwaysEnabled: true })}
                        />
                        {renderContactList(group, { hasChat: false })}
                      </div>
                    ) : (
                      <>
                        <RoleHeader
                          title={group.title}
                          hideUpdate={hideUpdate}
                          addButton={renderRoleAddButton(group, { alwaysEnabled: true })}
                        />
                        <div className="contact-role-empty text-gray-500 italic mt-2">
                          No contact found
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
